import type { Distance, SpatialRectangle } from "@pdfking/math"
import type { PageNumber } from "@pdfking/renderer-api"
import type { PdfEngine } from "../../types/engine/PdfEngine"
import type { TaskResult } from "../../types/tasks/input/TaskResult"
import type { TaskPlotOptions } from "../../types/tasks/options/TaskPlotOptions"
import type { TrackingCallback } from "../../types/tasks/tracking/TrackingCallback"
import { BlobArray } from "@pdfking/compatibility"
import { PDFPlot } from "@pdfking/renderer-default"
import { Items, IterableToArray, Map } from "@pdfking/tfunc"
import { GenerateFile } from "../../files/GenerateFile"
import { RequestBlob } from "../../files/RequestBlob"
import { ContentType } from "../../types/files/ContentType"
import { VirtualFileType } from "../../types/files/VirtualFileType"
import { TaskName } from "../../types/tasks/TaskName"
import { TrackedTaskName } from "../../types/tasks/tracking/TrackedTaskName"

// options PlotPdfOptions - file: Plot
// stream -
// emit TrackingCallback
//
// return DocumentPage[page, GeneratedFile[]] // takes the page number from PlotPdfOptions

export async function* TaskPlot(
  engine: PdfEngine,
  task: TaskPlotOptions,
  emit: TrackingCallback
): AsyncGenerator<TaskResult> {
  const emitter = (done: number) =>
    emit({
      done,
      name: TrackedTaskName.PLOT_PDF,
      total: 1,
    })

  void emitter(0)

  const content = await GenerateFile(
    engine,
    PDFPlot([task.page], {
      scale_bleed: task.options.scale_bleed,
      stroke_width: task.options.stroke_width,
      unit_to_px: task.options.unit_to_px,
      ...task.context,
      file_map: Object.fromEntries(
        Map(
          ([k, file]) => [
            k,
            RequestBlob(engine, file)
              .then(({ stream }) => IterableToArray(stream))
              .then(BlobArray),
          ],
          Items(task.context.file_map || {})
        )
      ),
    }),
    ContentType.PDF,
    emit
  )

  const bleed = task.page.bleed ?? task.context.page_bleed ?? (0 as Distance)
  const width = (task.page.width + bleed * 2) as Distance
  const height = (task.page.height + bleed * 2) as Distance

  const trim_box: SpatialRectangle<Distance> = {
    height: task.page.height,
    width: task.page.width,
    x: bleed,
    y: bleed,
  }

  const media_box: SpatialRectangle<Distance> = {
    height,
    width,
    x: 0 as Distance,
    y: 0 as Distance,
  }

  yield {
    content: {
      content,
      context: task.context,
      enum: VirtualFileType.SINGLE_PAGE_PDF,
      file_meta: {},
      media_box,
      page: task.page,
      page_count: 1 as PageNumber,
      page_number: 1 as PageNumber,
      size: media_box,
      source: undefined,
      trim_box,
    },
    enum: TaskName.CONTENT_FILE,
    name: "plot.pdf",
  }

  void emitter(1)
}
